import { AllActions } from '../../shared/redux/types';
import * as discussionActions from '../discussion/actions';
import { NormalizedSendingMessage } from '../discussion/types';
import * as inTripReviewActions from '../in-trip-review/actions';
import * as messageTemplatesActions from '../message-template/actions';
import * as preTripReviewActions from '../pre-trip-review/actions';

import { EntitiesState } from './types';

export const initialState = {
    agent: {},
    discussion: {},
    messageTemplate: {},
    status: {
        searchInArchived: undefined,
        discussion: {},
        messageTemplate: undefined,
    },
};

export const entitiesReducer = (
    state: EntitiesState = initialState,
    action: AllActions,
): EntitiesState => {
    switch (action.type) {
        case preTripReviewActions.fetchPreTripReviewsSuccessAction: {
            return {
                ...state,
                preTripReview: action.response,
            };
        }
        case inTripReviewActions.fetchInTripReviewsSuccessAction: {
            return {
                ...state,
                inTripReview: action.response,
            };
        }
        case preTripReviewActions.removePreTripReviewSuccessAction:
            return {
                ...state,
                preTripReview: undefined,
            };
        case inTripReviewActions.removeInTripReviewSuccessAction:
            return {
                ...state,
                inTripReview: undefined,
            };

        case messageTemplatesActions.getAllMessageTemplatesSuccessAction:
            return {
                ...state,
                messageTemplate: {
                    ...state.messageTemplate,
                    ...action.messageTemplates,
                },
            };
        case messageTemplatesActions.updateMessageTemplateSuccessAction:
            return {
                ...state,
                messageTemplate: {
                    ...state.messageTemplate,
                    ...action.messageTemplate,
                },
            };
        case messageTemplatesActions.postNewMessageTemplateSuccessAction:
            return {
                ...state,
                messageTemplate: {
                    ...state.messageTemplate,
                    ...action.messageTemplate,
                },
            };
        case messageTemplatesActions.deleteMessageTemplateRequestAction:
            return {
                ...state,
                messageTemplate: Object.fromEntries(
                    Object.entries(state.messageTemplate).filter(
                        ([, messageTemplate]) => messageTemplate.id !== action.id,
                    ),
                ),
            };
        case discussionActions.getDraftMessageSuccessAction:
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [action.dossierId]: {
                        ...state.discussion[action.dossierId],
                        draftMessage: action.draftMessage,
                        htmlDraftMessage: action.htmlDraftMessage,
                        draftMessageMetadata:
                            state.discussion[action.dossierId]?.draftMessageMetadata,
                    },
                },
            };
        case discussionActions.getMessageSuccessAction: {
            const { dossierId, sendingMessageId } = action;

            if (!sendingMessageId) {
                return state;
            }
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        draftMessage: undefined,
                        htmlDraftMessage: undefined,
                        draftMessageMetadata: undefined,
                        sendingMessage: {
                            ...state.discussion[dossierId].sendingMessage,
                            [sendingMessageId]: undefined,
                        },
                    },
                },
            };
        }
        case discussionActions.uploadAttachmentRequestAction: {
            const { dossierId, sendingMessageId, file, category } = action;
            const sendingMessage: NormalizedSendingMessage =
                state.discussion[dossierId]?.sendingMessage || {};
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        sendingMessage: {
                            ...sendingMessage,
                            [sendingMessageId]: {
                                content: sendingMessage[sendingMessageId]?.content || '',
                                contentHtml: sendingMessage[sendingMessageId]?.contentHtml || '',
                                id: sendingMessageId,
                                attachments: {
                                    ...sendingMessage[sendingMessageId]?.attachments,
                                    [file.name]: {
                                        file: file,
                                        category: category,
                                    },
                                },
                            },
                        },
                    },
                },
            };
        }
        case discussionActions.uploadAttachmentSuccessAction: {
            const { attachmentId, dossierId, sendingMessageId, fileName } = action;
            const sendingMessage: NormalizedSendingMessage =
                state.discussion[dossierId]?.sendingMessage || {};
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        sendingMessage: {
                            ...sendingMessage,
                            [sendingMessageId]: {
                                content: sendingMessage[sendingMessageId]?.content || '',
                                contentHtml: sendingMessage[sendingMessageId]?.contentHtml || '',
                                id: sendingMessageId,
                                attachments: {
                                    ...sendingMessage[sendingMessageId]?.attachments,
                                    [fileName]: {
                                        file: sendingMessage[sendingMessageId]!.attachments?.[
                                            fileName
                                        ].file as File,
                                        category:
                                            sendingMessage[sendingMessageId]?.attachments?.[
                                                fileName
                                            ].category || null,
                                        id: attachmentId.toString(),
                                    },
                                },
                            },
                        },
                    },
                },
            };
        }
        case discussionActions.sendMessageRequestAction: {
            const { dossierId, content, contentHtml, sendingMessageId } = action;
            const sendingMessage: NormalizedSendingMessage =
                state.discussion[dossierId]?.sendingMessage || {};
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        sendingMessage: {
                            ...sendingMessage,
                            [sendingMessageId]: {
                                ...sendingMessage[sendingMessageId],
                                content,
                                contentHtml,
                                id: sendingMessageId,
                            },
                        },
                        draftMessage: '',
                        htmlDraftMessage: '',
                        draftMessageMetadata: undefined,
                    },
                },
            };
        }
        case discussionActions.deleteAttachmentAction: {
            const { dossierId, fileName, sendingMessageId } = action;
            const sendingMessage = state.discussion[dossierId]?.sendingMessage || {};
            const attachments =
                state.discussion[dossierId]?.sendingMessage?.[sendingMessageId]?.attachments || {};
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        sendingMessage: {
                            ...sendingMessage,
                            [sendingMessageId]: {
                                content: sendingMessage[sendingMessageId]?.content || '',
                                contentHtml: sendingMessage[sendingMessageId]?.contentHtml || '',
                                id: sendingMessageId,
                                attachments: Object.fromEntries(
                                    Object.entries(attachments).filter(
                                        ([, attachment]) => attachment.file.name !== fileName,
                                    ),
                                ),
                            },
                        },
                    },
                },
            };
        }
        case discussionActions.saveDraftMessageAction: {
            const { draftMessage, htmlDraftMessage, dossierId, metadata } = action;
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        draftMessage: draftMessage,
                        htmlDraftMessage: htmlDraftMessage,
                        draftMessageMetadata: metadata,
                    },
                },
            };
        }
        case discussionActions.scheduleMessageSuccessAction: {
            const {
                dossierId,
                content,
                contentHtml,
                scheduledDate,
                id,
                authorId,
                sendingMessageId,
            } = action;
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        draftMessage: undefined,
                        htmlDraftMessage: undefined,
                        draftMessageMetadata: undefined,
                        scheduledMessage: {
                            id,
                            content,
                            contentHtml,
                            scheduledDate,
                            authorId,
                        },
                        ...(sendingMessageId && {
                            sendingMessage: {
                                ...state.discussion[dossierId].sendingMessage,
                                [sendingMessageId]: undefined,
                            },
                        }),
                    },
                },
            };
        }
        case discussionActions.fetchScheduledMessagesSuccessAction: {
            const { dossierId, content, contentHtml, scheduledDate, id, authorId, attachments } =
                action;
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        scheduledMessage: {
                            id,
                            content,
                            contentHtml,
                            scheduledDate,
                            authorId,
                            attachments,
                        },
                    },
                },
            };
        }
        case discussionActions.deleteScheduledMessageSuccessAction: {
            const { dossierId } = action;
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [dossierId]: {
                        ...state.discussion[dossierId],
                        scheduledMessage: null,
                    },
                },
            };
        }
        case discussionActions.getDiscussionAttachmentsSuccessAction:
            return {
                ...state,
                discussion: {
                    ...state.discussion,
                    [action.dossierId]: {
                        ...state.discussion[action.dossierId],
                        attachments: action.messageAttachments,
                    },
                },
            };
        default:
            return state;
    }
};
